import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ScenarioLayout from './components/ScenarioLayout';
import ScenarioReport from './components/ScenarioReport';
import AIInsights from './components/AIInsights';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  createLedLightingSchema,
  createRooftopSolarSchema,
  createHvacSchema,
  createTemperatureReductionSchema,
  createGreenTariffPpaSchema,
  createFleetElectrificationSchema,
} from './validateSchema';

import { getScenarioDefinitions, SCENARIO_FORM_MAP } from './scenarioConfig';
import { getCurrentReportPeriod, getReportRangePeriod } from '../reports/reportHelper';
import { generateScenarioOutputApi, getScenarioBaselineApi } from './scenarioApi';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { parseUserInfo } from '../../utilities/common';
import SaveScenarioModal from './components/SaveScenarioModal';

const ScenarioPage = () => {
  const { scenarioKey } = useParams();
  const [baseline, setBaseline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSave, setShowSave] = useState(false);
  const { t } = useTranslation();

  const scenarios = getScenarioDefinitions(t);
  const config = scenarios[scenarioKey];

  const { orgId, usrTimezone, ...userInfo } = parseUserInfo(useSelector((state) => state.users.user));
  const currentReportPeriod = getCurrentReportPeriod(userInfo.usrOrgOptions);
  const reportRangePeriod = getReportRangePeriod(userInfo.usrOrgOptions, currentReportPeriod.reportYear);

  const schemaMap = {
    led: createLedLightingSchema(t),
    solar: createRooftopSolarSchema(t),
    hvac: createHvacSchema(t),
    temp: createTemperatureReductionSchema(t),
    ppa: createGreenTariffPpaSchema(t),
    fleet: createFleetElectrificationSchema(t),
  };

  useEffect(() => {
    if (
      !scenarioKey
      || !reportRangePeriod.startDate
      || scenarioKey === baseline?.model
    ) return;
    getScenarioBaselineApi(orgId, {
      model: scenarioKey,
      startDate: reportRangePeriod.startDate,
      endDate: reportRangePeriod.endDate,
      usrTimezone,
    })
      .then(res => {
        setBaseline(res);
      })
      .catch(() => {
        toast.error(t('Failed to load CarbonView data for this scenario.'));
      })
  }, [
    scenarioKey,
    baseline?.model,
    orgId,
    reportRangePeriod.startDate,
    reportRangePeriod.endDate,
    usrTimezone,
    t,
  ]);

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schemaMap[scenarioKey]),
  });
  const { handleSubmit } = form;

  const [result, setResult] = useState(null);

  const FormComponent = SCENARIO_FORM_MAP[scenarioKey];

  if (!FormComponent || !config) {
    return <div>{t('Scenario not supported')}</div>;
  }

  const onGenerate = async (values) => {
    try {
      setIsLoading(true);
      const payload = {
        model: scenarioKey,
        startDate: reportRangePeriod.startDate,
        endDate: reportRangePeriod.endDate,
        usrTimezone,
        inputs: values,
      };
      const calculated = await generateScenarioOutputApi(orgId, payload);
      setResult(calculated);
    } catch (e) {
      toast.error(t('Failed to generate output for this scenario.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = handleSubmit(onGenerate);

  return (
    <ScenarioLayout
      title={config.title}
      subtitle={config.subtitle}
      icon={config.icon}
      color={config.color}
      onGenerate={handleGenerate}
      onSave={() => setShowSave(true)}
      disableSave={!result}
      isLoading={isLoading}
    >
      <FormProvider {...form}>
        <FormComponent
          isLoading={isLoading}
          baseline={baseline?.model === scenarioKey ? baseline : null}
        />
      </FormProvider>

      {result && (
        <>
          <ScenarioReport
            result={result?.model === scenarioKey ? result : null}
          />
          <AIInsights insights={result.aiInsights} />
        </>
      )}
      <SaveScenarioModal
        show={showSave}
        onClose={() => setShowSave(false)}
        orgId={orgId}
        model={scenarioKey}
        handleSubmit={handleSubmit}
        inputs={{
          startDate: reportRangePeriod.startDate,
          endDate: reportRangePeriod.endDate,
          usrTimezone,
        }}
        baseline={baseline}
        result={result}
      />
    </ScenarioLayout>
  );
};

export default ScenarioPage;
